import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button, cx } from "./ui";

import type { Role } from "../lib/types";

interface NavItem {
  to: string;
  label: string;
  roles: Role[];
  /**
   * An owner reports as an admin, so a role list cannot express "owner only".
   * This can, and it is the only place in the interface that needs to.
   */
  ownerOnly?: true;
  /**
   * Hidden from an operator, which reports as an admin and so cannot be
   * excluded by a role list either. Courtesy only — the API refuses these
   * routes whatever the sidebar draws.
   */
  custodyOnly?: true;
}

interface NavGroup {
  /** What the person is doing, not what the tables are called. */
  heading: string;
  items: NavItem[];
}

/**
 * Three audiences share this shell, and each is shown only what its own work
 * needs. A marketer has no licences to look at; a customer has no reason to
 * learn that a Signing keys screen exists.
 *
 * Grouped by the job rather than by entity. Someone opening this app is either
 * getting their bearings, chasing money, granting or withdrawing access, or
 * correcting a record, and those are the four piles.
 */
const NAV: NavGroup[] = [
  {
    heading: "Overview",
    items: [
      // Everyone lands somewhere that tells them where they stand.
      { to: "/overview", label: "Overview", roles: ["admin", "customer", "marketer"] },
      { to: "/dashboard", label: "Reporting", roles: ["admin"] },
    ],
  },
  {
    heading: "Money",
    items: [
      { to: "/orders", label: "Orders", roles: ["admin", "customer"] },
      { to: "/orders", label: "My sales", roles: ["marketer"] },
      // A customer who has bought once should not have to go back to the
      // marketing site and retype everything to buy again.
      { to: "/plans", label: "Order something", roles: ["customer"] },
      { to: "/commission", label: "Commission", roles: ["admin", "marketer"] },
      { to: "/payment-links", label: "Payment links", roles: ["admin", "marketer"] },
      { to: "/reports", label: "Reports", roles: ["admin"] },
    ],
  },
  {
    heading: "Custody",
    items: [
      { to: "/licenses", label: "Licences", roles: ["admin", "customer"], custodyOnly: true },
      { to: "/signing-keys", label: "Signing keys", roles: ["admin"], custodyOnly: true },
      // Who holds the keys is custody too, and it is the owner who decides.
      { to: "/accounts", label: "Staff accounts", roles: ["admin"], ownerOnly: true },
      // The trail records every licence and key action, so reading it is
      // reading custody by another route.
      { to: "/audit", label: "Audit", roles: ["admin"], custodyOnly: true },
    ],
  },
  {
    heading: "Records",
    items: [
      { to: "/projects", label: "Projects", roles: ["admin", "customer"] },
      { to: "/leads", label: "Leads", roles: ["admin"] },
      { to: "/customers", label: "Customers", roles: ["admin"] },
      { to: "/marketers", label: "Marketers", roles: ["admin"] },
      { to: "/catalogue", label: "Products & plans", roles: ["admin"] },
      { to: "/blog", label: "Blog", roles: ["admin"] },
    ],
  },
];

/**
 * Below this, headings label piles of one and stop being navigation aids.
 *
 * Raised from five when every role gained an Overview link: that put a
 * customer on exactly five links spread across four groups, so three of the
 * four headings would have labelled a single item each — which is the filing
 * this rule exists to prevent. An admin has fourteen and genuinely needs them.
 */
const HEADINGS_EARN_THEIR_PLACE_AT = 6;

const ROLE_LABELS: Record<Role, string | null> = {
  admin: null,
  customer: "Customer",
  marketer: "Marketer",
};

export function AppShell() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const role: Role = user?.role ?? "customer";
  const roleLabel = ROLE_LABELS[role];

  const isOwner = user?.isOwner ?? false;
  // Defaults to true so an older session, or a fetch that has not landed yet,
  // shows the section it always showed rather than blinking it away.
  const canSeeLicensing = user?.canSeeLicensing ?? true;
  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) =>
        item.roles.includes(role) &&
        (item.ownerOnly !== true || isOwner) &&
        (item.custodyOnly !== true || canSeeLicensing),
    ),
  })).filter((group) => group.items.length > 0);

  const total = groups.reduce((count, group) => count + group.items.length, 0);
  const showHeadings = total >= HEADINGS_EARN_THEIR_PLACE_AT;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Focus follows the panel in. Without it the keyboard is still back on the
  // bar underneath, and the first Tab goes somewhere invisible.
  useEffect(() => {
    if (drawerOpen) closeRef.current?.focus();
  }, [drawerOpen]);

  // Navigating is the whole reason the drawer was opened, so arriving closes
  // it. Without this a mobile user lands on the new screen behind the panel
  // they just used.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setDrawerOpen(false);
        // Focus goes back where it came from, or it lands on the page body and
        // a keyboard user has to tab from the top again.
        toggleRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    // The page behind must not scroll under the drawer.
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [drawerOpen]);

  const nav = (
    <Nav groups={groups} showHeadings={showHeadings} onNavigate={closeDrawer} />
  );

  const identity = (
    // Settings sits with the account rather than in a section: it is about the
    // person signed in, not about the business the sections describe.
    <div className="border-t border-hairline p-3">
      <p className="truncate text-sm text-ink" title={user?.email}>
        {user?.email}
      </p>
      <p className="mt-0.5 text-[0.6875rem] uppercase tracking-[0.12em] text-dim">
        {roleLabel ?? "Staff"}
      </p>
      <div className="mt-2 flex items-center gap-1">
        <NavLink
          to="/settings"
          onClick={closeDrawer}
          className={({ isActive }) =>
            cx(
              "rounded-md px-2 py-1.5 text-sm transition-colors",
              isActive ? "bg-raised text-ink" : "text-muted hover:text-ink",
            )
          }
        >
          Settings
        </NavLink>
        <Button variant="ghost" className="px-2" onClick={() => void signOut()}>
          Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
      {/* Desktop: always there, and it does not scroll away from you. */}
      <aside className="sticky top-0 hidden h-dvh flex-col border-r border-hairline bg-void lg:flex">
        <Wordmark />
        {nav}
        {identity}
      </aside>

      {/* Mobile: the bar is the only chrome until it is asked for. */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-hairline bg-void/95 px-4 py-3 backdrop-blur lg:hidden">
        <button
          ref={toggleRef}
          type="button"
          aria-expanded={drawerOpen}
          aria-controls="app-nav"
          // Stable name. `aria-expanded` already announces the state, and a
          // control that renames itself as it is used is read out as a
          // different control each time.
          aria-label="Menu"
          onClick={() => setDrawerOpen((open) => !open)}
          className="-ml-1 rounded p-2 text-muted transition-colors hover:text-ink"
        >
          <MenuIcon open={drawerOpen} />
        </button>
        <span className="text-sm font-semibold tracking-tight">
          ServiceFinder<span className="text-dim"> / licensing</span>
        </span>
      </header>

      {drawerOpen && (
        // Tapping away is a convenience for a thumb, not a control: Escape and
        // the close button are the real ways out. Announcing it as a second
        // "Close menu" button would just mean hearing the same control twice.
        <div
          aria-hidden
          onClick={closeDrawer}
          className="fixed inset-0 z-30 bg-void/70 backdrop-blur-sm lg:hidden"
        />
      )}

      <div
        id="app-nav"
        // `inert` would be better, but React 18 does not forward it. Hidden
        // from assistive tech and untabbable when closed, which is the part
        // that matters: a closed drawer must not be reachable by keyboard.
        aria-hidden={!drawerOpen}
        className={cx(
          "fixed inset-y-0 left-0 z-40 flex w-[17rem] max-w-[85vw] flex-col border-r border-hairline bg-void lg:hidden",
          "transition-[transform,visibility] duration-200 motion-reduce:transition-none",
          // `invisible` rather than only a transform: a panel slid off screen
          // is still in the tab order, so a keyboard user would tab into a
          // menu they cannot see.
          drawerOpen ? "visible translate-x-0" : "invisible -translate-x-full",
        )}
      >
        {/* The drawer covers the bar it was opened from, so it carries its own
            way out. Backdrop and Escape both work, but neither is visible. */}
        <div className="flex items-center justify-between gap-2 border-b border-hairline px-4 py-3">
          <span className="text-sm font-semibold tracking-tight">
            ServiceFinder<span className="text-dim"> / licensing</span>
          </span>
          <button
            ref={closeRef}
            type="button"
            onClick={() => {
              closeDrawer();
              toggleRef.current?.focus();
            }}
            aria-label="Close menu"
            className="-mr-2 rounded p-2 text-muted transition-colors hover:text-ink"
          >
            <MenuIcon open />
          </button>
        </div>
        {nav}
        {identity}
      </div>

      {/* Wide enough to fill a laptop, bounded so a table on an ultrawide does
          not stretch into unreadable line lengths. */}
      <main className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
        <Outlet />
      </main>
    </div>
  );
}

function Wordmark() {
  return (
    <div className="hidden items-center border-b border-hairline px-4 py-4 lg:flex">
      <span className="text-sm font-semibold tracking-tight">
        ServiceFinder<span className="text-dim"> / licensing</span>
      </span>
    </div>
  );
}

/**
 * The keyline: a rail on the sidebar's inner edge, aligned to whichever section
 * you are in. It reads as the current section seated into the content beside
 * it, which is what this system does to a licence — holds it, or lets it go.
 *
 * Measured rather than drawn per item so it travels between sections instead of
 * blinking out and in.
 */
function Nav({
  groups,
  showHeadings,
  onNavigate,
}: {
  groups: NavGroup[];
  showHeadings: boolean;
  onNavigate: () => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [rail, setRail] = useState<{ top: number; height: number } | null>(null);
  const location = useLocation();

  useLayoutEffect(() => {
    const container = listRef.current;
    if (container === null) return;

    const measure = () => {
      // NavLink marks the active route with aria-current itself, so the rail
      // and the highlight read the same source and cannot drift apart.
      const active = container.querySelector<HTMLElement>("a[aria-current='page']");
      if (active === null) {
        setRail(null);
        return;
      }
      setRail({ top: active.offsetTop, height: active.offsetHeight });
    };

    measure();

    // The sidebar scrolls and the drawer animates in, so a single measurement
    // taken at the wrong moment lands the rail beside nothing.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [location.pathname, groups.length]);

  return (
    <nav aria-label="Sections" className="relative flex-1 overflow-y-auto py-3">
      <div ref={listRef} className="relative">
        {rail !== null && (
          <span
            aria-hidden
            className="absolute right-0 w-[2px] rounded-l bg-accent transition-[transform,height] duration-200 ease-out motion-reduce:transition-none"
            style={{ transform: `translateY(${rail.top}px)`, height: rail.height }}
          />
        )}

        {groups.map((group) => (
          <div key={group.heading} className="mb-1 last:mb-0">
            {showHeadings && (
              <h2 className="px-4 pb-1 pt-3 text-[0.625rem] font-medium uppercase tracking-[0.16em] text-dim">
                {group.heading}
              </h2>
            )}
            <ul>
              {group.items.map((item) => (
                <li key={item.label}>
                  <NavLink
                    to={item.to}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cx(
                        "block px-4 py-2 text-sm transition-colors",
                        isActive
                          ? "bg-raised font-medium text-ink"
                          : "text-muted hover:bg-surface hover:text-ink",
                      )
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </nav>
  );
}

/** Two lines that become a cross. Drawn rather than imported — one icon does
 *  not justify a dependency shipped to every admin. */
function MenuIcon({ open }: { open: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d={open ? "M5 5 L15 15" : "M3 6 H17"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d={open ? "M15 5 L5 15" : "M3 12 H17"}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
